import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { ColumnDef, SortingState } from "@/components/ui/data-table";
import { DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	CENNIKI_WLASNE_PAGE_SIZE,
	useCennikiWlasne,
} from "@/features/strateg-cenowy/hooks/use-cenniki-wlasne";
import { fetchPricing } from "@/features/strateg-cenowy/strateg-api";
import type { PricingItem, PricingSort } from "@/features/strateg-cenowy/types";
import { useStrategScope } from "@/features/strateg-cenowy/use-strateg-scope";
import { ensureElementVisible } from "@/lib/route-guards";
import { encodeScope } from "@/lib/scope";
import { useDebounce } from "@/lib/use-debounce";

const SORT_FROM_COL: Record<string, PricingSort> = {
	pharmacyName: "pharmacy",
	bloz: "bloz",
	productName: "product",
	cenaDetaliczna: "cena",
	cennikName: "cennik",
	dataAktualizacji: "data_akt",
};

const COL_FROM_SORT: Record<PricingSort, string> = {
	pharmacy: "pharmacyName",
	bloz: "bloz",
	product: "productName",
	cena: "cenaDetaliczna",
	cennik: "cennikName",
	data_akt: "dataAktualizacji",
};

export const Route = createFileRoute("/_auth/strateg-cenowy/cenniki-wlasne")({
	component: CennikiWlasneRoute,
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.strateg-cenowy.cenniki-wlasne",
			location.pathname,
		);
	},
});

function CennikiWlasneRoute() {
	const { scope } = useStrategScope();
	const [searchRaw, setSearchRaw] = useState("");
	const search = useDebounce(searchRaw.trim(), 300);
	const [sort, setSort] = useState<PricingSort | null>("cennik");
	const [dir, setDir] = useState<"asc" | "desc">("asc");

	const query = useCennikiWlasne({ scope, search, sort, dir });

	const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data?.pages]);
	const total = query.data?.pages[query.data.pages.length - 1]?.total;

	const handleFetchMore = useCallback(() => {
		if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
	}, [query]);

	const sortingState: SortingState = useMemo(
		() => (sort ? [{ id: COL_FROM_SORT[sort], desc: dir === "desc" }] : []),
		[sort, dir],
	);

	const handleSortingChange = useCallback(
		(updater: SortingState | ((s: SortingState) => SortingState)) => {
			const next = typeof updater === "function" ? updater(sortingState) : updater;
			const first = next[0];
			if (!first) {
				// 3-stan: ASC -> DESC -> NONE. TanStack daje [] gdy user kliknie 3 raz.
				setSort(null);
				return;
			}
			const mapped = SORT_FROM_COL[first.id];
			if (!mapped) return;
			setSort(mapped);
			setDir(first.desc ? "desc" : "asc");
		},
		[sortingState],
	);

	const fetchAllForExport = useCallback(async (): Promise<Record<string, unknown>[]> => {
		// HARD_CAP 100k = backend offset clamp; przekroczenie zwraca duplikaty.
		const HARD_CAP = 100_000;
		const allItems: PricingItem[] = [];
		const scopeKey = encodeScope(scope);
		let offset = 0;
		while (offset < HARD_CAP) {
			const page = await fetchPricing({
				scope: scopeKey,
				search: search || undefined,
				sort: sort ?? undefined,
				dir,
				limit: CENNIKI_WLASNE_PAGE_SIZE,
				offset,
			});
			allItems.push(...page.items);
			if (page.items.length < CENNIKI_WLASNE_PAGE_SIZE) break;
			if (allItems.length >= page.total) break;
			offset += CENNIKI_WLASNE_PAGE_SIZE;
		}
		if (allItems.length >= HARD_CAP) {
			// eslint-disable-next-line no-alert
			// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
			alert(
				`Eksport przerwany na ${HARD_CAP.toLocaleString("pl-PL")} wierszach (limit). ` +
					`Zaweż scope albo użyj wyszukiwarki aby zmniejszyć zestaw.`,
			);
		}
		return allItems.map((it) => ({
			pharmacyName: it.pharmacyName,
			bloz: it.bloz,
			productName: it.productName ?? "",
			dosage: it.dosage ?? "",
			pack: it.pack ?? "",
			cenaDetaliczna: it.cenaDetaliczna,
			cennikName: it.cennikName ?? "",
			dataAktualizacji: it.dataAktualizacji ?? "",
		}));
	}, [scope, search, sort, dir]);

	const columns = useMemo<ColumnDef<PricingItem>[]>(
		() => [
			{
				id: "pharmacyName",
				accessorKey: "pharmacyName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Apteka" />,
				enableSorting: true,
				// Lista na telefonie: towar = tytul, apteka + cennik = podtytul,
				// cena = miara (bez `isNumeric`, ktore zmienia wyrownanie na desktopie).
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "bloz",
				accessorKey: "bloz",
				header: ({ column }) => <DataTableColumnHeader column={column} title="BLOZ" />,
				enableSorting: true,
				cell: ({ row }) => <span className="font-mono text-xs">{row.original.bloz}</span>,
			},
			{
				id: "productName",
				accessorKey: "productName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Nazwa towaru" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.productName ?? <span className="text-muted-foreground">—</span>,
				meta: { mobileRole: "title" },
			},
			{
				id: "dosage",
				accessorKey: "dosage",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dawka" />,
				enableSorting: false,
				cell: ({ row }) => row.original.dosage ?? <span className="text-muted-foreground">—</span>,
			},
			{
				id: "pack",
				accessorKey: "pack",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Opakowanie" />,
				enableSorting: false,
				cell: ({ row }) => row.original.pack ?? <span className="text-muted-foreground">—</span>,
			},
			{
				id: "cenaDetaliczna",
				accessorKey: "cenaDetaliczna",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Cena detaliczna" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.cenaDetaliczna != null ? (
						<span className="tabular-nums">{row.original.cenaDetaliczna.toFixed(2)}</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
				meta: { mobileRole: "measure", mobileLabel: "Cena" },
			},
			{
				id: "cennikName",
				accessorKey: "cennikName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Cennik" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.cennikName ?? <span className="text-muted-foreground">—</span>,
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "dataAktualizacji",
				accessorKey: "dataAktualizacji",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Data aktualizacji" />,
				enableSorting: true,
				cell: ({ row }) =>
					row.original.dataAktualizacji != null ? (
						<span className="tabular-nums">{row.original.dataAktualizacji}</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
		],
		[],
	);

	return (
		<div className="flex flex-col h-full p-3 md:p-4 gap-3 md:gap-4">
			<div className="flex flex-wrap items-end gap-3 md:gap-4">
				<div className="flex flex-col gap-1 w-full max-w-sm">
					<Label htmlFor="cenniki-search" className="text-xs text-muted-foreground">
						Szukaj (nazwa towaru lub BLOZ)
					</Label>
					<Input
						id="cenniki-search"
						value={searchRaw}
						onChange={(e) => setSearchRaw(e.target.value)}
						placeholder="np. APAP albo 0007891"
					/>
				</div>
				<div className="text-xs text-muted-foreground ml-auto pb-2">
					{total != null ? `${items.length} z ${total} wierszy` : "Wczytywanie..."}
				</div>
			</div>

			<DataTable<PricingItem>
				tableId="strateg-cenowy-cenniki-wlasne"
				columns={columns}
				data={items}
				isLoading={(query.isFetching || query.isPending) && !query.isFetchingNextPage}
				loadingLabel="Wczytywanie cennikow..."
				totalCount={total}
				sorting={sortingState}
				onSortingChange={handleSortingChange}
				manualSorting
				onFetchMore={handleFetchMore}
				hasNextPage={query.hasNextPage}
				isFetchingNextPage={query.isFetchingNextPage}
				enableToolbar
				enableColumnVisibility
				enableColumnPinning
				enableSchemes
				enableColumnColoring
				defaultPinnedColumns={["pharmacyName"]}
				columnLabels={{
					pharmacyName: "Apteka",
					bloz: "BLOZ",
					productName: "Nazwa towaru",
					dosage: "Dawka",
					pack: "Opakowanie",
					cenaDetaliczna: "Cena detaliczna",
					cennikName: "Cennik",
					dataAktualizacji: "Data aktualizacji",
				}}
				showSummaryRow
				summableColumns={["cenaDetaliczna"]}
				summaryLabel="Sredia / Suma"
				deriveSummary={({ sums, rowCount }) => ({
					pharmacyName: `${rowCount} pozycji`,
					cenaDetaliczna:
						rowCount > 0
							? `sr. ${(sums.cenaDetaliczna / rowCount).toFixed(2)} (suma ${sums.cenaDetaliczna.toFixed(2)})`
							: "—",
				})}
				enableExport
				exportFileName="cenniki-wlasne"
				fetchAllForExport={fetchAllForExport}
				className="flex-1 min-h-0"
			/>
		</div>
	);
}
